import { QueryClient } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Link,
  createRootRouteWithContext,
  useLocation,
  useRouter,
} from "@tanstack/react-router";
import { Suspense, useEffect, useRef, useState } from "react";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { SiteNav } from "../components/SiteNav";
import { SiteFooter } from "../components/SiteFooter";
import { CookieConsentBanner } from "../components/CookieConsentBanner";
import { FloatingWhatsApp } from "../components/FloatingWhatsApp";
import { useContent } from "../lib/use-content";
import { wasPrerendered } from "../lib/prerender-hydration";
import { captureUtmFromUrl } from "../lib/lead-attribution";
import { trackWhatsappClick, appendWhatsappOriginCode, registerDirectWhatsappLead } from "../lib/cta-analytics";

function NotFoundComponent() {
  // La URL inexistente responde 200 (el rewrite de Hosting entrega el index): `noindex` evita que
  // Google la trate como página real (soft-404). Se quita al salir para no afectar otras rutas.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    meta.setAttribute("data-cp-404", "1");
    document.head.appendChild(meta);
    return () => { meta.remove(); };
  }, []);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página no encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          La página que buscas no existe o fue movida.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}

/** Después de cada deploy, Vite renombra los archivos JS de cada sección (hash nuevo). Una
 *  pestaña que ya estaba abierta desde ANTES del deploy todavía tiene en memoria los nombres
 *  viejos, así que al navegar a una sección (ej. Planificación) intenta pedir un archivo que
 *  ya no existe en el servidor y esto se ve como una pantalla de error genérica. No es un bug
 *  de datos ni de la sección en sí — se soluciona solo con una recarga completa. */
function isStaleChunkError(error: Error): boolean {
  return /fetch dynamically imported module|importing a module script failed|error loading dynamically imported module/i.test(error.message);
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "root_error_component" });
    if (isStaleChunkError(error)) {
      const key = "cp_stale_chunk_reload_at";
      const lastReload = Number(sessionStorage.getItem(key) || 0);
      // Solo un reload automático cada 10s, para no entrar en loop si el archivo
      // sigue faltando por otra razón real.
      if (Date.now() - lastReload > 10000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página no cargó
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo salió mal. Intenta recargar o vuelve al inicio.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Intentar de nuevo
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Ir al inicio
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

/** Detecta una pestaña abierta desde ANTES del último deploy y la recarga sola. El aviso de
 *  chunk faltante (ver isStaleChunkError) solo se dispara si el navegador intenta pedir un
 *  archivo nuevo y no lo encuentra — pero una pestaña que se queda quieta usando código ya
 *  cargado en memoria nunca pide nada nuevo, así que nunca dispara ese error: sigue
 *  funcionando con la versión vieja indefinidamente (así le pasó al profesor al otorgarle
 *  cofres a Joche y abrirlos desde una pestaña vieja — el cofre seguía dando solo
 *  potenciadores porque corría la lógica de antes de agregar Fondos/Efectos). Compara el
 *  script principal ya cargado contra el que referencia el index.html fresco del servidor
 *  (sin caché) cada 5 minutos y cada vez que la pestaña vuelve a primer plano; si difieren,
 *  hubo un deploy nuevo y se recarga sola. En desarrollo (sin archivos con hash) no hace nada. */
function useAutoReloadOnStaleDeploy() {
  useEffect(() => {
    const myScript = document.querySelector('script[type="module"][src*="/assets/index-"]')?.getAttribute("src");
    if (!myScript) return;
    let cancelled = false;
    const checkForNewVersion = async () => {
      try {
        const res = await fetch("/index.html", { cache: "no-store" });
        const html = await res.text();
        const match = html.match(/<script[^>]*type="module"[^>]*src="([^"]*\/assets\/index-[^"]*)"/);
        if (!cancelled && match && match[1] && match[1] !== myScript) window.location.reload();
      } catch {
        // Red intermitente — se reintenta solo en el próximo ciclo, sin romper nada ahora.
      }
    };
    const interval = setInterval(() => { if (document.visibilityState === "visible") checkForNewVersion(); }, 5 * 60 * 1000);
    const onVisible = () => { if (document.visibilityState === "visible") checkForNewVersion(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { cancelled = true; clearInterval(interval); document.removeEventListener("visibilitychange", onVisible); };
  }, []);
}

/** Aviso de navegación para Google Analytics (ver el snippet de gtag.js en index.html, conexión
 *  DIRECTA a GA4 sin pasar por Google Tag Manager) — la carga inicial ya la cuenta sola el
 *  gtag('config', ...) del index.html, pero como el sitio es una SPA que nunca vuelve a cargar
 *  la página completa al cambiar de sección, sin este aviso Analytics nunca se entera de las
 *  navegaciones siguientes. Se salta el primer render a propósito (esa carga inicial ya la
 *  cuenta el config de arriba, contarla de nuevo acá la duplicaría) — solo manda el evento en
 *  cambios REALES de ruta. No se activa dentro de /staff, /portal ni /admin (paneles privados,
 *  ya excluidos de robots.txt): mezclar clics de alumnos/profesores usando la app con visitas
 *  reales de gente buscando el gimnasio arruinaría cualquier métrica de marketing. */
function useGa4Pageview(pathname: string, href: string) {
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const isPrivateArea = pathname.startsWith("/admin") || pathname.startsWith("/staff") || pathname.startsWith("/portal");
    if (isPrivateArea) return;
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (!w.gtag) return;
    // window.location.href NO sirve acá — TanStack Router actualiza su propio estado (y por lo
    // tanto vuelve a correr este efecto) un instante ANTES de que el navegador termine de
    // aplicar el pushState real, así que leerlo en este momento devuelve la URL ANTERIOR
    // (verificado con un test real: quedaba un page_view apuntando siempre a la página de la
    // que se venía, no a la nueva). El `href` de useLocation() ya viene actualizado al
    // instante — solo hace falta pegarle el origin (ese sí es estable, nunca cambia en una
    // navegación del lado del cliente).
    w.gtag("event", "page_view", { page_path: pathname, page_location: `${window.location.origin}${href}`, page_title: document.title });
  }, [pathname, href]);
}

function RootComponent() {
  useAutoReloadOnStaleDeploy();
  const { pathname, href } = useLocation();
  useGa4Pageview(pathname, href);
  // Captura utm_source/utm_medium/utm_campaign/?ref= al aterrizar (Fase 3 del CRM de leads,
  // ver lib/lead-attribution.ts) — una sola vez por carga completa de página, igual que
  // useGa4Pageview arriba lo hace para el page_view inicial.
  useEffect(() => { captureUtmFromUrl(); }, []);
  // Bug real (CLS Suspense-fallback más arriba en este archivo + hallazgo nuevo de la fase de
  // prerender extendido, ver CLAUDE.md): hidratar un `<Suspense>` sobre HTML que NO vino de un
  // render de servidor real (`renderToString`/streaming) tira "Minified React error #418" — un
  // `<Suspense>` necesita comentarios especiales (`<!--$-->`/`<!--/$-->`) en el HTML para que
  // React sepa cómo reconciliarlo al hidratar, y esos comentarios SOLO los emite un render de
  // servidor de verdad, nunca un HTML capturado con un navegador headless (lo que hace
  // scripts/prerender-content.mjs) — pasa sin importar si el `<Suspense>` termina suspendiéndose
  // o no en ese primer render. Confirmado con `onRecoverableError` (componentStack apuntando
  // exactamente a este `<Suspense>`) y descartado que `router.load()` lo arregle solo (precarga
  // el chunk de la ruta, pero el problema es el HTML en sí, no si el componente está listo).
  // Fix: saltear el `<Suspense>` SOLO en el primer render (cuando `<div id="root">` ya venía con
  // contenido real) — `main.tsx` ya esperó `router.load()` antes de hidratar, así que el chunk
  // de la ruta actual ya está resuelto y `<Outlet/>` no necesita suspenderse en ese primer
  // render. Un `useEffect` vuelve a poner el `<Suspense>` de inmediato después (mismo tick que el
  // usuario nunca llega a ver) para que las rutas SIN prerender (o una navegación del lado del
  // cliente a otra ruta después) sigan protegidas por el fix de CLS de arriba.
  const [skipSuspenseForHydration, setSkipSuspenseForHydration] = useState(wasPrerendered);
  useEffect(() => {
    if (skipSuspenseForHydration) setSkipSuspenseForHydration(false);
  }, [skipSuspenseForHydration]);
  // /admin (CMS del sitio), /staff (CP Admin) y /portal (Portal del Estudiante) son paneles
  // internos de pantalla completa con su propio shell — no llevan el nav/footer del sitio
  // público. /padres (portal de padres de Kidstenia) y /programa-kidstenia (landing pública de
  // Kidstenia) también entran acá porque arman su propio nav/footer con la identidad visual de
  // Kidstenia (navy/cyan/magenta), distinta del resto del sitio — llevar el nav/footer público
  // encima se vería duplicado y con la paleta equivocada. /kidstenia se mantiene acá solo porque
  // el shim de redirect también vive bajo pantalla completa (evita el parpadeo del nav público
  // durante el instante del redirect).
  const isAppShell =
    pathname.startsWith("/admin") || pathname.startsWith("/staff") || pathname.startsWith("/portal")
    || pathname.startsWith("/kidstenia") || pathname.startsWith("/padres") || pathname.startsWith("/programa-kidstenia")
    // /mi-programa (portal de un programa digital, ej. Muscle Up) arma su propio header —
    // /programas/{slug} (landing pública del mismo programa) sí lleva el nav/footer normales.
    || pathname.startsWith("/mi-programa")
    // /consentimiento (Fase 8 legal — Anexo A) y /hoja-de-ruta (Evaluación y Hoja de Ruta, Fase 3
    // — "modo presentación") son páginas públicas sin login que abren desde un enlace de correo/
    // WhatsApp: el nav/footer del sitio (con enlaces a Inicio, Clases, etc.) solo distraería de
    // la única acción que importa acá. Bug real encontrado con una impresión/PDF real (capturas
    // del dueño): sin esto, /hoja-de-ruta/$token quedaba envuelto en el nav y el footer público
    // completos — en pantalla se notaba poco (el contenido real llena la mayor parte del scroll),
    // pero al imprimir/exportar a PDF el footer entero (enlaces, redes, copyright) se imprimía
    // como una página extra y el nav agregaba una franja negra arriba, además de hacer que el
    // contenido real se viera chico (el navegador ajusta la escala de impresión a TODO lo que hay
    // en la página, nav y footer incluidos).
    || pathname.startsWith("/consentimiento") || pathname.startsWith("/hoja-de-ruta");

  // Páginas públicas sin login que cargan GA4 (index.html) y por eso también piden consentimiento:
  // enlaces por token de /consentimiento y /hoja-de-ruta. /programa-kidstenia monta su propio
  // banner (paleta Kidstenia) dentro de kidstenia-landing.tsx. NO lo llevan /admin, /staff, /portal,
  // /padres ni /mi-programa (paneles con sesión) ni /kidstenia (solo redirige): ahí GA4 queda
  // denegado por defecto y la elección se hace en las páginas públicas.
  const showTokenPageBanner = pathname.startsWith("/consentimiento") || pathname.startsWith("/hoja-de-ruta");

  if (isAppShell) {
    return (
      <div className="min-h-screen bg-black text-white">
        <HeadContent />
        {skipSuspenseForHydration ? <Outlet /> : (
          <Suspense fallback={<AppShellFallback />}>
            <Outlet />
          </Suspense>
        )}
        {showTokenPageBanner && <CookieConsentBanner />}
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <HeadContent />
      <SiteNav />
      <main className="flex-1 pt-16 min-h-[calc(100vh-4rem)]">
        {skipSuspenseForHydration ? <Outlet /> : (
          <Suspense fallback={<PageFallback />}>
            <Outlet />
          </Suspense>
        )}
      </main>
      <SiteFooter />
      <SiteWhatsAppButton />
      {/* Aviso de cookies de analítica (idea 286). Layout público de CP; Kidstenia lo monta
          en su propia página (paleta propia) y los paneles no lo llevan (GA4 queda denegado). La
          elección vale para todo el dominio. */}
      <CookieConsentBanner />
    </div>
  );
}

// Botón flotante de WhatsApp del sitio principal (no Kidstenia, que arma el suyo propio dentro
// de kidstenia-landing.tsx) — reusa el mismo canal ya editable desde /admin → Contacto en vez
// de tener un teléfono/mensaje hardcodeados acá.
function SiteWhatsAppButton() {
  const channels = useContent<{ items: { k: string; href: string }[] }>("contacto_channels");
  const whatsapp = channels.items.find((c) => c.k === "WhatsApp");
  if (!whatsapp) return null;
  return (
    <FloatingWhatsApp
      href={appendWhatsappOriginCode(whatsapp.href, "(web-float)")}
      theme="cp"
      trackKey="cp_whatsapp_float"
      onWhatsappClick={() => { registerDirectWhatsappLead("botón flotante"); trackWhatsappClick("cp_whatsapp_float"); }}
    />
  );
}

/** Bug real (CLS 0.922 confirmado con PageSpeed Insights en producción, "Causantes del cambio
 *  de diseño" apuntando al `<main>` completo). Reproducido en el sandbox con un logger propio de
 *  rAF (no solo con capturas — el salto es demasiado rápido para verlo a simple vista incluso con
 *  throttling): en una carga en frío (entrar directo a una ruta, no una navegación dentro del
 *  sitio), `<main>` llega a pintar al menos un frame real con CERO hijos — ni el contenido de la
 *  ruta ni el `fallback` de este `Suspense` (el primer intento de fix, darle un `fallback` con su
 *  propio alto reservado, NO alcanzó: React puede pintar ese frame vacío antes de que el
 *  `fallback` siquiera llegue a montarse, así que un alto puesto SOLO en el fallback no protege
 *  nada — verificado con el mismo logger, el bug seguía idéntico byte a byte con el fallback ya
 *  puesto). Antes, sin ningún alto mínimo, ese frame vacío colapsaba `main` (flex-1) a solo su
 *  `padding-top` (64px), empujando `<SiteFooter/>` justo debajo del nav, visible en el viewport —
 *  al llegar el contenido real un instante después, `main` saltaba de esos 64px a su alto real
 *  (miles de píxeles en una página de varias secciones), de ahí el 0.922 (`defaultPreload:
 *  "intent"` en router.tsx ya precarga el chunk de cada ruta al pasar el mouse/foco por un link
 *  de navegación, pero eso no ayuda en una carga directa/en frío, que es justo lo que mide
 *  PageSpeed). **El fix real es el `min-h-[calc(100vh-4rem)]` en el propio `<main>`** (ver su
 *  className más abajo) — un `min-height` en el elemento en sí no depende de qué hijo (o ninguno)
 *  llegue a pintarse primero, a diferencia de reservar alto solo en el `fallback`. Bajó el CLS de
 *  0.922 a ~0.076 en las 10 rutas públicas (footer moviéndose un resto de 64px que sí queda
 *  parcialmente visible en el viewport inicial — no se pudo bajar más sin arriesgar un salto en
 *  sentido contrario en páginas cortas). Este `fallback` sigue de respaldo por si el hueco dura
 *  lo suficiente para pintarse (red más lenta que la de estas pruebas). */
function PageFallback() {
  return <div className="min-h-[calc(100vh-4rem)]" aria-hidden="true" />;
}

// /admin, /staff y /portal se descargan aparte del sitio público (code-split, ver
// lazyRouteComponent en cada route.tsx) — esto se ve brevemente mientras llega ese bundle.
function AppShellFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-white animate-spin" />
    </div>
  );
}
